// Minimal, dependency-free single-page PDF writer.
//
// Why hand-rolled: this was built in a sandbox with no npm registry access
// (installing jspdf/jspdf-autotable returned 403), so this writes the raw
// PDF file format directly instead. If you'd rather maintain it with a real
// library, `npm install jspdf` and swap the receipt builder in
// utils/pdfReceipt.js over to it.
//
// Uses the standard (non-embedded) Helvetica / Helvetica-Bold PDF fonts,
// which only support Latin-1 text — the peso sign (₱) is written as "P"
// since it falls outside that range.

import { hexToRgb } from './colorUtil';

// Adobe standard AFM character widths (1/1000 em), codes 32-126.
const HELVETICA_WIDTHS = [278,278,355,556,556,889,667,191,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,278,278,584,584,584,556,1015,667,667,722,722,667,611,778,722,278,500,667,556,833,722,778,667,778,722,667,611,722,667,944,667,667,611,278,278,278,469,556,333,556,556,500,556,556,278,556,556,222,222,500,222,833,556,556,556,556,333,500,278,556,500,722,500,500,500,334,260,334,584];
const HELVETICA_BOLD_WIDTHS = [278,333,474,556,556,889,722,238,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,333,333,584,584,584,611,975,722,722,722,722,667,611,778,722,278,556,722,611,833,722,778,667,778,722,667,611,722,667,944,667,667,611,333,278,333,584,556,333,556,611,556,611,556,333,611,611,278,278,556,278,889,611,611,611,611,389,556,333,611,556,778,556,556,500,389,280,389,584];

function widthOf(code, bold) {
  const table = bold ? HELVETICA_BOLD_WIDTHS : HELVETICA_WIDTHS;
  if (code < 32 || code > 126) return 556;
  return table[code - 32];
}

export function textWidthPt(text, bold, size) {
  let units = 0;
  for (let i = 0; i < text.length; i++) units += widthOf(text.charCodeAt(i), bold);
  return (units / 1000) * size;
}

// Strips/replaces anything the base14 Latin-1 fonts can't render, and
// escapes PDF string-literal special characters.
function pdfText(str) {
  return String(str ?? '')
    .replace(/₱/g, 'P')
    // Other non-Latin-1 currency symbols a club might configure
    // (Config -> System). £, €, ¥ and ¢ are already in WinAnsi and pass
    // through the strip below untouched, so they are deliberately absent here.
    .replace(/₹/g, 'Rs')
    .replace(/₩/g, 'W')
    .replace(/₦/g, 'N')
    .replace(/₫/g, 'd')
    .replace(/₪/g, 'ILS')
    .replace(/฿/g, 'B')
    // Map the punctuation the app actually produces to an ASCII equivalent
    // BEFORE the catch-all strip below, which would otherwise delete it and
    // leave a gap: "P5 × 6 hrs" became "P5  6 hrs", and a package's
    // "3:00 PM – 7:00 PM" lost its dash.
    .replace(/[×]/g, 'x')
    .replace(/[–—]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

const rgbTuple = (color) => color.map(c => Math.round(c * 1000) / 1000).join(' ');
const num = (value) => value.toFixed(2);

// Bezier control-point distance that makes four cubic curves approximate a
// circle (4/3 * (sqrt(2) - 1)); PDF has no native circle operator.
const CIRCLE_KAPPA = 0.5523;

/**
 * Converts a "#aabbcc" colour to the 0..1 RGB tuple PDF fill operators take.
 * @param {string} hex - a colour already checked with isValidHex
 * @returns {number[]} [r, g, b], each 0..1
 */
export function hexToPdfColor(hex) {
  const { r, g, b } = hexToRgb(hex);
  return [r / 255, g / 255, b / 255];
}

/**
 * Splits text into lines that fit a width, breaking on spaces.
 * @param {string} text
 * @param {number} maxWidth - available width in points
 * @param {{size?: number, bold?: boolean}} [font]
 * @returns {string[]} the wrapped lines (a word longer than maxWidth gets its own line)
 */
export function wrapText(text, maxWidth, { size = 11, bold = false } = {}) {
  const lines = [];
  let current = '';
  String(text ?? '').split(/\s+/).filter(Boolean).forEach(word => {
    const candidate = current ? `${current} ${word}` : word;
    if (current && textWidthPt(pdfText(candidate), bold, size) > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });
  if (current) lines.push(current);
  return lines;
}

/**
 * One page of drawing operations. Coordinates are top-left based (y grows
 * downward, like the DOM) and flipped to PDF's bottom-left origin here.
 */
export class PdfPage {
  constructor(width, height) {
    this.width = width;
    this.height = height;
    this.ops = '';
    this.images = [];
  }
  rect(x, y, w, h, color) {
    this.ops += `${rgbTuple(color)} rg\n${num(x)} ${num(this.height - y - h)} ${num(w)} ${num(h)} re f\n`;
    return this;
  }
  line(x1, y1, x2, y2, color, lineWidth = 1) {
    this.ops += `${rgbTuple(color)} RG\n${lineWidth} w\n${num(x1)} ${num(this.height - y1)} m ${num(x2)} ${num(this.height - y2)} l S\n`;
    return this;
  }
  text(x, y, str, { size = 11, bold = false, color = [0, 0, 0], align = 'left' } = {}) {
    const safe = pdfText(str);
    let drawX = x;
    if (align === 'right') drawX = x - textWidthPt(safe, bold, size);
    else if (align === 'center') drawX = x - textWidthPt(safe, bold, size) / 2;
    const font = bold ? '/F2' : '/F1';
    this.ops += `BT\n${rgbTuple(color)} rg\n${font} ${size} Tf\n${num(drawX)} ${num(this.height - y)} Td\n(${safe}) Tj\nET\n`;
    return this;
  }

  /**
   * Path operators for a circle (not painted — callers fill or clip it).
   * @param {number} cx - centre x
   * @param {number} cy - centre y (top-left based)
   * @param {number} r - radius
   * @returns {string}
   */
  circlePath(cx, cy, r) {
    const y = this.height - cy;
    const k = r * CIRCLE_KAPPA;
    return `${num(cx + r)} ${num(y)} m\n`
      + `${num(cx + r)} ${num(y + k)} ${num(cx + k)} ${num(y + r)} ${num(cx)} ${num(y + r)} c\n`
      + `${num(cx - k)} ${num(y + r)} ${num(cx - r)} ${num(y + k)} ${num(cx - r)} ${num(y)} c\n`
      + `${num(cx - r)} ${num(y - k)} ${num(cx - k)} ${num(y - r)} ${num(cx)} ${num(y - r)} c\n`
      + `${num(cx + k)} ${num(y - r)} ${num(cx + r)} ${num(y - k)} ${num(cx + r)} ${num(y)} c\n`;
  }

  /** Fills a circle. Same arguments as circlePath plus a 0..1 RGB colour. */
  circle(cx, cy, r, color) {
    this.ops += `${rgbTuple(color)} rg\n${this.circlePath(cx, cy, r)}f\n`;
    return this;
  }

  /**
   * Draws a JPEG masked to a circle — the round logo the site headers use.
   * @param {number} cx - centre x
   * @param {number} cy - centre y (top-left based)
   * @param {number} r - radius
   * @param {{bytes: Uint8Array, width: number, height: number}} image - from loadImageAsJpeg()
   * @returns {PdfPage}
   */
  circleImage(cx, cy, r, image) {
    this.images.push(image);
    const name = `Im${this.images.length}`;
    const size = r * 2;
    // q/Q scope the clip so it doesn't swallow everything drawn afterwards.
    this.ops += `q\n${this.circlePath(cx, cy, r)}W n\n${num(size)} 0 0 ${num(size)} ${num(cx - r)} ${num(this.height - cy - r)} cm\n/${name} Do\nQ\n`;
    return this;
  }
}

/**
 * Serialises a page into a one-page PDF.
 *
 * Built from byte chunks rather than one string because embedded JPEGs are
 * binary: a JS string handed to Blob is UTF-8 encoded, which would corrupt
 * every byte above 0x7F. Offsets in the xref table are byte offsets for the
 * same reason.
 * @param {PdfPage} page
 * @returns {Blob} application/pdf
 */
export function pageToPdfBlob(page) {
  const encoder = new TextEncoder();
  const content = encoder.encode(page.ops);
  const firstImageObj = 7;
  const xObjects = page.images.length
    ? ` /XObject << ${page.images.map((_, i) => `/Im${i + 1} ${firstImageObj + i} 0 R`).join(' ')} >>`
    : '';

  // Each object is a list of parts (strings or raw bytes).
  const objects = [
    ['<< /Type /Catalog /Pages 2 0 R >>'],
    ['<< /Type /Pages /Kids [3 0 R] /Count 1 >>'],
    [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${page.width} ${page.height}] /Resources << /Font << /F1 4 0 R /F2 5 0 R >>${xObjects} >> /Contents 6 0 R >>`],
    ['<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>'],
    ['<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>'],
    [`<< /Length ${content.length} >>\nstream\n`, content, '\nendstream'],
    ...page.images.map(img => [
      `<< /Type /XObject /Subtype /Image /Width ${img.width} /Height ${img.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${img.bytes.length} >>\nstream\n`,
      img.bytes,
      '\nendstream',
    ]),
  ];

  const chunks = [];
  let size = 0;
  const push = (part) => {
    const bytes = typeof part === 'string' ? encoder.encode(part) : part;
    chunks.push(bytes);
    size += bytes.length;
  };

  push('%PDF-1.4\n');
  // Binary-marker comment: tells PDF tools the file holds binary streams.
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));
  const offsets = [];
  objects.forEach((parts, i) => {
    offsets.push(size);
    push(`${i + 1} 0 obj\n`);
    parts.forEach(push);
    push('\nendobj\n');
  });
  const xrefStart = size;
  push(`xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`);
  offsets.forEach(off => push(`${String(off).padStart(10, '0')} 00000 n \n`));
  push(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`);
  return new Blob(chunks, { type: 'application/pdf' });
}

/**
 * Loads an image and re-encodes it as a square JPEG for embedding.
 *
 * Squared with object-fit: cover maths (what BrandLogo's CSS does) so it
 * crops the same way the site's round logo does. Transparent areas are
 * painted with `background` first because JPEG has no alpha — passing the
 * header colour makes a transparent logo sit on the header exactly as it
 * does on the site.
 * @param {string} src - data: URL (Admin Config upload) or same-origin path
 * @param {{size?: number, background?: string}} [options] - pixel size, CSS colour
 * @returns {Promise<{bytes: Uint8Array, width: number, height: number} | null>}
 *   null when the image can't be loaded or read, so the caller can simply
 *   leave the logo out instead of failing the whole download.
 */
export async function loadImageAsJpeg(src, { size = 240, background = '#ffffff' } = {}) {
  if (!src) return null;
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      // Lets a CORS-enabled remote URL be read back from the canvas; data:
      // and same-origin URLs (the normal cases) are unaffected.
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Logo image failed to load'));
      el.src = src;
    });
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, size, size);
    const scale = Math.max(size / img.naturalWidth, size / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) return null;
    return { bytes: new Uint8Array(await blob.arrayBuffer()), width: size, height: size };
  } catch {
    // A missing/tainted logo is cosmetic; the receipt is still useful without it.
    return null;
  }
}
